import type { Home, HomeDto } from '@lmc/ts-common'
import {
  DownloadFile,
  EmptyState,
  FilterContainer,
  GroupedUsersMultiSelect,
  NoDataIcon,
  PageHeader,
  RangePicker
} from '@lmc/ui-kit'
import type { TablePaginationConfig } from 'antd/es/table'
import type { SorterResult } from 'antd/es/table/interface'
import { canManagePayAndInvoicing } from 'common/permissions'
import { getNominative } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import dayjs, { type Dayjs } from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  TravelRateCardLevel,
  TravelRateCardStatus,
  useGetTravelRateCardSettings
} from 'screens/Rostering/hooks/useTravelRateCardSettings'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import {
  getReconciliationRangeDisabledDate,
  isValidReconciliationDateRange,
  RECONCILIATION_MAX_PAST_MONTHS
} from 'screens/Rostering/Reconciliation/reconciliationUtils'
import { DownloadPayReportModal } from '../components/payReport/DownloadPayReportModal'
import { PayReportBreakdownDrawer } from '../components/payReport/PayReportBreakdownDrawer'
import { PayReportCsvButton } from '../components/payReport/PayReportBreakdownFooter'
import {
  payReportTableComponents,
  StyledPayReportTable
} from '../components/payReport/PayReportTableComponents'
import {
  ContentContainer,
  Filters,
  LeftContainer,
  LoaderWrap,
  MainContainer,
  RightAlign,
  StyledMultiSelect,
  TableScroll
} from '../components/payReport/payReportPageStyles'
import { usePayReport } from '../hooks/usePayReport'
import { getPayReportColumns } from '../utils/payReportColumns'
import { createContractTypeLabelResolver } from '../utils/payReportContractUtils'
import type {
  PayReportRow,
  PayReportSortField,
  PayReportToReconciliationNavigationState
} from '../utils/payReportTypes'

const PAGE_SIZE = 10

const PayReport = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs().subtract(1, 'day')
  ])
  const [teamMembers, setTeamMembers] = useState<string[]>([])
  const [contractTypes, setContractTypes] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [sortField, setSortField] =
    useState<PayReportSortField>('teamMemberName')
  const [sortDir, setSortDir] = useState<1 | -1>(1)
  const [breakdownRow, setBreakdownRow] = useState<PayReportRow | null>(null)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const hasAbilityLoaded = useSelector(
    (state: { global: { ability: unknown } }) => state.global.ability != null
  )
  const userRole = useSelector(
    (state: { global: { user: { role: string } } }) => state.global.user?.role
  )
  // Pay report is available to admins/management too (ROST-3440), in addition
  // to anyone granted the PayRate view ability.
  const canViewPayRate =
    useAbilityAccess([{ i: 'view', this: 'PayRate' }]) ||
    canManagePayAndInvoicing(userRole)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const serviceUserLabel = getNominative(preferredPeopleNominative, 'Resident')

  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: ['payrates', 'travel_and_mileage']
  })
  const payratesFlagEnabled = flags?.payrates?.enabled === true
  const travelAndMileageFlagEnabled =
    flags?.travel_and_mileage?.enabled === true
  const { data: travelRateCards } = useGetTravelRateCardSettings()
  const siteTravelRateCard = travelRateCards?.find(
    (card) => card.levelType === TravelRateCardLevel.SITE
  )
  const travelFeatureEnabled =
    travelAndMileageFlagEnabled &&
    siteTravelRateCard?.status === TravelRateCardStatus.ENABLED
  const showTravelTimePayColumn =
    travelFeatureEnabled && Boolean(siteTravelRateCard?.timePayRate)
  const showMileagePayColumn =
    travelFeatureEnabled && Boolean(siteTravelRateCard?.mileagePayRate)

  const { users } = useUsersList({ active: true })

  const contractTypeOptions =
    home.rotaSettings?.contractTypes?.map((ct) => ({
      label: ct.name,
      value: ct.id
    })) ?? []

  const resolveContractTypeLabel = useMemo(
    () => createContractTypeLabelResolver(home.rotaSettings?.contractTypes),
    [home.rotaSettings?.contractTypes]
  )

  const rangeValid = useMemo(
    () => isValidReconciliationDateRange(dateRange[0], dateRange[1]),
    [dateRange]
  )

  const navigateToReconciliation = useCallback(
    (teamMemberId: string) => {
      if (!isValidReconciliationDateRange(dateRange[0], dateRange[1])) {
        return
      }
      navigate('/rota/reconciliation', {
        state: {
          fromPayReport: true,
          teamMemberIds: [teamMemberId],
          startTime: dateRange[0].startOf('day').toISOString(),
          endTime: dateRange[1].endOf('day').toISOString()
        } satisfies PayReportToReconciliationNavigationState
      })
    },
    [dateRange, navigate]
  )

  const apiFilters = useMemo(() => {
    if (!rangeValid) {
      return null
    }
    return {
      startTime: dateRange[0].startOf('day').toISOString(),
      endTime: dateRange[1].endOf('day').toISOString(),
      teamMemberIds: teamMembers,
      ...(contractTypes.length ? { contractTypes } : {}),
      page,
      pageSize: PAGE_SIZE,
      sortField,
      sortDir
    }
  }, [
    rangeValid,
    dateRange,
    teamMembers,
    contractTypes,
    page,
    sortField,
    sortDir
  ])

  const queryEnabled = Boolean(payratesFlagEnabled && apiFilters !== null)

  const { data, isLoading, isFetching } = usePayReport(apiFilters, queryEnabled)

  const rows = data?.data ?? []
  const total = data?.pagination?.totalItems ?? 0

  const columns = useMemo(
    () =>
      getPayReportColumns({
        t,
        sortField,
        sortDir,
        navigateToReconciliation,
        resolveContractTypeLabel,
        showTravelTimePayColumn,
        showMileagePayColumn
      }),
    [
      t,
      sortField,
      sortDir,
      navigateToReconciliation,
      resolveContractTypeLabel,
      showTravelTimePayColumn,
      showMileagePayColumn
    ]
  )

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates?.[1]) {
      return
    }
    if (!isValidReconciliationDateRange(dates[0], dates[1])) {
      return
    }
    setDateRange([dates[0], dates[1]])
    setPage(1)
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<PayReportRow> | SorterResult<PayReportRow>[],
    extra: { action: 'paginate' | 'sort' | 'filter' }
  ) => {
    if (extra.action === 'paginate') {
      if (pagination.current != null) {
        setPage(pagination.current)
      }
    } else {
      setPage(1)
    }
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.columnKey && s.order) {
      setSortField(String(s.columnKey) as PayReportSortField)
      setSortDir(s.order === 'ascend' ? 1 : -1)
    }
  }

  const isAccessStateResolving =
    isFlagsLoading || flags?.payrates == null || !hasAbilityLoaded

  if (isAccessStateResolving) {
    return (
      <LoaderWrap>
        <Spinner />
      </LoaderWrap>
    )
  }

  if (!payratesFlagEnabled || !canViewPayRate) {
    return <Navigate to="/reports/rostering/rota-overview" replace />
  }

  return (
    <MainContainer>
      <PageHeader title={t('PAY_REPORT_TITLE')} />
      <FilterContainer title={t('FILTERS')}>
        <Filters>
          <LeftContainer>
            <RangePicker
              value={dateRange}
              onChange={handleRangeChange}
              disabledDate={getReconciliationRangeDisabledDate}
              allowClear={false}
              format="DD-MM-YYYY"
              minDate={dayjs().subtract(
                RECONCILIATION_MAX_PAST_MONTHS,
                'month'
              )}
              maxDate={dayjs()}
            />
            <GroupedUsersMultiSelect
              users={users}
              title={t('TEAM_MEMBERS')}
              values={teamMembers}
              handleInputChange={(selected) => {
                setTeamMembers(selected as string[])
                setPage(1)
              }}
            />
            <StyledMultiSelect
              title={t('CONTRACT_TYPES')}
              options={contractTypeOptions}
              values={contractTypes}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selected) => {
                setContractTypes(selected as string[])
                setPage(1)
              }}
            />
          </LeftContainer>
          <RightAlign>
            <PayReportCsvButton
              icon={<DownloadFile />}
              onClick={() => setDownloadModalOpen(true)}
            >
              {t('PAY_REPORT_DOWNLOAD_CSV')}
            </PayReportCsvButton>
          </RightAlign>
        </Filters>
      </FilterContainer>

      <ContentContainer>
        {!rangeValid ? (
          <EmptyState
            title={t('PAY_REPORT_INVALID_RANGE')}
            icon={<NoDataIcon />}
          />
        ) : (isLoading || isFetching) && !rows.length ? (
          <LoaderWrap>
            <Spinner />
          </LoaderWrap>
        ) : !rows.length && !isFetching ? (
          <EmptyState title={t('NO_DATA_TO_SHOW')} icon={<NoDataIcon />} />
        ) : (
          <TableScroll>
            <StyledPayReportTable
              components={payReportTableComponents}
              columns={columns}
              dataSource={rows}
              rowKey="teamMemberId"
              loading={isFetching}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total,
                showSizeChanger: false,
                position: ['bottomCenter']
              }}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              size="small"
              showSorterTooltip={false}
              sortDirections={['ascend', 'descend']}
              rowClassName={(_, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              onRow={(record) => ({
                'data-testid': 'pay-report-row',
                'data-tm-id': record.teamMemberId,
                onClick: () => setBreakdownRow(record),
                style: { cursor: 'pointer' }
              })}
            />
          </TableScroll>
        )}
      </ContentContainer>
      <PayReportBreakdownDrawer
        key={
          breakdownRow
            ? `${breakdownRow.teamMemberId}-${dateRange[0].valueOf()}-${dateRange[1].valueOf()}`
            : 'pay-report-breakdown-closed'
        }
        open={breakdownRow !== null}
        onClose={() => setBreakdownRow(null)}
        dateRange={dateRange}
        row={breakdownRow}
        showTravelTimePayColumn={showTravelTimePayColumn}
        showMileagePayColumn={showMileagePayColumn}
      />
      <DownloadPayReportModal
        isVisible={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        initialDateRange={dateRange}
        serviceUserLabel={serviceUserLabel}
      />
    </MainContainer>
  )
}

export default PayReport
